import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import type { AuthErrorKey } from '@/lib/authErrors';
import { oauthProviders } from '@/lib/config';
import type { OAuthProvider } from '@/lib/oauthPending';
import { cn } from '@/lib/utils';
import { PROVIDER_ICONS } from './ProviderIcons';

/**
 * "Continue with Google" / "Continue with Apple" (design section 5). Which ones show comes from the
 * runtime config (Google unless switched off, Apple only when switched on); none at all renders nothing.
 * A click hands the page to the provider: both buttons stay disabled while the browser leaves, and a
 * refusal that came back before any redirect re-enables them and goes to `onError`.
 */
export function OAuthButtons({
  redirectPath,
  from,
  onError,
  className,
}: {
  /** The landing path the provider returns to ('/signin', '/invite/<id>'). */
  redirectPath: string;
  /** Where to go after signing in; kept across the round trip. */
  from?: string | null;
  onError(key: AuthErrorKey): void;
  className?: string;
}) {
  const { t } = useTranslation();
  const { signInWithProvider } = useAuth();
  const [pending, setPending] = React.useState<OAuthProvider | null>(null);
  // Back from the provider restores this page from the back-forward cache with `pending` still set:
  // both buttons would stay disabled on "Opening Google…". A persisted pageshow means we never left.
  React.useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) setPending(null);
    }
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  const enabled = oauthProviders();
  const providers = (['google', 'apple'] as const).filter((p) => enabled[p]);
  if (providers.length === 0) return null;

  async function start(provider: OAuthProvider) {
    setPending(provider);
    const { error } = await signInWithProvider(provider, redirectPath, { from });
    // Success: the browser is on its way to the provider, the buttons stay as they are.
    if (!error) return;
    setPending(null);
    onError(error);
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {providers.map((provider) => {
        const Icon = PROVIDER_ICONS[provider];
        return (
          <Button
            key={provider}
            type="button"
            variant="outline"
            className="h-touch w-full gap-3"
            disabled={pending !== null}
            aria-busy={pending === provider || undefined}
            onClick={() => void start(provider)}
          >
            <Icon />
            {pending === provider
              ? t('auth.oauth.redirecting', { provider: t(`profile.methods.provider.${provider}`) })
              : t(`auth.oauth.${provider}`)}
          </Button>
        );
      })}
    </div>
  );
}

/** "or" between the provider buttons and an email form, with a hairline on each side. */
export function OrDivider({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div role="separator" aria-label={t('auth.oauth.or')} className={cn('flex items-center gap-3', className)}>
      <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
      <span className="text-sm text-ink-muted">{t('auth.oauth.or')}</span>
      <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
    </div>
  );
}
